import { z } from "zod";
import {
  MAX_BATCH_SHIPMENTS,
  MAX_TRACK_SELECTION,
  PACKAGE_MAX_SIDE_CM,
  PACKAGE_MAX_WEIGHT_G,
  REVERSE_DAYS_DEFAULT,
  REVERSE_DAYS_MAX,
  REVERSE_DAYS_MIN,
  SHIPMENT_STATUSES,
  type LogisticsProvider,
  type ShipmentStatus,
} from "@cx/domain";
import { listQuery } from "./common";
import { ulidParam } from "./ids";
import { businessDate } from "./prices";

// ===== Logística (T3.4, RF-VEN-10) =====

export type { LogisticsProvider, ShipmentStatus };

const nullish = <T extends z.ZodType>(s: T) => s.nullish().transform((v) => v ?? null);
const text = (max: number, what: string) =>
  z
    .string(`Informe ${what}`)
    .trim()
    .min(1, `Informe ${what}`)
    .max(max, `${what[0]!.toUpperCase()}${what.slice(1)}: até ${max} caracteres`);
const cents = (what: string) =>
  z
    .number(`Informe ${what}`)
    .int("Use centavos inteiros")
    .min(0, `${what} não pode ser negativo`)
    .max(100_000_000_00, `${what} acima do limite`);
const intIn = (min: number, max: number, msg: string) => z.number(msg).int(msg).min(min, msg).max(max, msg);

/** Pacote em inteiros: peso em gramas, medidas em centímetros (limites finos no domínio, 422 `invalid_package`). */
export const packageInput = z.object({
  weightG: intIn(1, PACKAGE_MAX_WEIGHT_G, `Peso: de 1 a ${PACKAGE_MAX_WEIGHT_G} g`),
  heightCm: intIn(1, PACKAGE_MAX_SIDE_CM, `Altura: de 1 a ${PACKAGE_MAX_SIDE_CM} cm`),
  widthCm: intIn(1, PACKAGE_MAX_SIDE_CM, `Largura: de 1 a ${PACKAGE_MAX_SIDE_CM} cm`),
  lengthCm: intIn(1, PACKAGE_MAX_SIDE_CM, `Comprimento: de 1 a ${PACKAGE_MAX_SIDE_CM} cm`),
});
export type PackageInput = z.output<typeof packageInput>;

const serviceCode = z
  .string("Informe o serviço")
  .trim()
  .regex(/^[0-9A-Za-z_-]{1,20}$/, "Serviço inválido");

/** Configuração da integração (serviços só nos Correios: os do Melhor Envio vêm da cotação). */
export const logisticsSettingsInput = z.object({
  services: z
    .array(
      z.object({
        code: z
          .string("Informe o código")
          .trim()
          .regex(/^\d{5}$/, "Código do serviço: 5 dígitos"),
        name: text(40, "o nome do serviço"),
      }),
    )
    .max(10, "Até 10 serviços")
    .default([])
    .refine((l) => new Set(l.map((s) => s.code)).size === l.length, "Serviço repetido"),
  defaultPackage: packageInput.nullable().default(null),
  /** Declara o valor dos produtos (seguro/valor declarado). */
  declareValue: z.boolean().default(true),
  reverseDays: intIn(
    REVERSE_DAYS_MIN,
    REVERSE_DAYS_MAX,
    `Validade da reversa: de ${REVERSE_DAYS_MIN} a ${REVERSE_DAYS_MAX} dias`,
  ).default(REVERSE_DAYS_DEFAULT),
});
export type LogisticsSettingsInput = z.output<typeof logisticsSettingsInput>;
export type LogisticsSettings = LogisticsSettingsInput;

/** Credenciais do CWS dos Correios (nunca voltam na leitura). */
export const correiosCredentialsInput = z.object({
  user: text(100, "o usuário do Meu Correios"),
  accessCode: text(200, "o código de acesso à API"),
  postingCard: z
    .string("Informe o cartão de postagem")
    .trim()
    .regex(/^\d{1,20}$/, "Cartão de postagem: só números"),
  contract: z
    .string("Informe o contrato")
    .trim()
    .regex(/^\d{1,20}$/, "Contrato: só números"),
  dr: nullish(intIn(1, 99, "DR: de 1 a 99")),
});
export type CorreiosCredentialsInput = z.output<typeof correiosCredentialsInput>;

export const correiosIntegrationInput = z.object({
  name: text(60, "o nome"),
  credentials: correiosCredentialsInput,
  settings: logisticsSettingsInput.default({
    services: [],
    defaultPackage: null,
    declareValue: true,
    reverseDays: REVERSE_DAYS_DEFAULT,
  }),
});
export type CorreiosIntegrationInput = z.output<typeof correiosIntegrationInput>;

export const logisticsIntegrationUpdateInput = z.object({
  name: text(60, "o nome"),
  settings: logisticsSettingsInput,
  /** Correios: trocar as credenciais (conferidas na hora). */
  credentials: correiosCredentialsInput.optional(),
});
export type LogisticsIntegrationUpdateInput = z.output<typeof logisticsIntegrationUpdateInput>;

/** Volta do OAuth do Melhor Envio: `code` e o `state` gerado na autorização (32 bytes em base64url). */
export const melhorEnvioCallbackInput = z.object({
  code: z
    .string("Código ausente")
    .min(1, "Código ausente")
    .max(4096, "Código inválido")
    .regex(/^[A-Za-z0-9_.~+/=-]+$/, "Código inválido"),
  state: z.string("Retorno inválido").regex(/^[A-Za-z0-9_-]{43}$/, "Retorno inválido"),
});
export type MelhorEnvioCallbackInput = z.infer<typeof melhorEnvioCallbackInput>;
export const melhorEnvioSandboxAuthorizeInput = melhorEnvioCallbackInput.pick({ state: true });

export type LogisticsIntegrationView = {
  id: string;
  provider: LogisticsProvider;
  name: string;
  status: "active" | "reauth_required" | "inactive";
  sandbox: boolean;
  externalName: string | null;
  settings: LogisticsSettings;
  lastError: string | null;
  lastTrackedAt: string | null;
  createdAt: string;
  activeShipments: number;
};
export type LogisticsOverview = {
  integrations: LogisticsIntegrationView[];
  modes: Record<LogisticsProvider, "sandbox" | "live" | "disabled">;
};

// ---------- cotação ----------

export const shipmentQuoteInput = z
  .object({
    integrationId: ulidParam,
    salesOrderId: ulidParam.optional(),
    /** Sem pedido: CEP de destino (8 dígitos), pacote e valor declarado. */
    toCep: z
      .string()
      .transform((s) => s.replace(/\D/g, ""))
      .pipe(z.string().regex(/^\d{8}$/, "CEP inválido"))
      .optional(),
    package: packageInput.optional(),
    declaredValueCents: cents("o valor declarado").optional(),
  })
  .refine((v) => !!v.salesOrderId || !!v.toCep, { message: "Informe o pedido ou o CEP", path: ["toCep"] });
export type ShipmentQuoteInput = z.output<typeof shipmentQuoteInput>;

export type ShippingQuoteOption = {
  serviceCode: string;
  serviceName: string;
  carrierName: string | null;
  priceCents: number | null;
  deliveryDays: number | null;
  error: string | null;
};
export type ShippingQuote = { integrationId: string; options: ShippingQuoteOption[] };

// ---------- objetos de postagem ----------

export const shipmentCreateInput = z.object({
  salesOrderId: ulidParam,
  integrationId: ulidParam,
  serviceCode,
  serviceName: text(60, "o nome do serviço"),
  carrierName: nullish(z.string().trim().max(60, "Transportadora: até 60 caracteres")),
  quotedCents: nullish(cents("o valor cotado")),
  deliveryDays: nullish(intIn(0, 365, "Prazo inválido")),
  package: packageInput,
  /** null = pela configuração da integração (total dos produtos ou zero). */
  declaredValueCents: nullish(cents("o valor declarado")),
  notes: nullish(z.string().trim().max(500, "Observações: até 500 caracteres")),
});
export type ShipmentCreateInput = z.output<typeof shipmentCreateInput>;

export const shipmentListQuery = listQuery.extend({
  from: businessDate.optional(),
  to: businessDate.optional(),
  status: z.enum(SHIPMENT_STATUSES, "Situação inválida").optional(),
  integrationId: ulidParam.optional(),
  batchId: ulidParam.optional(),
  salesOrderId: ulidParam.optional(),
  reverse: z.enum(["yes", "no"]).optional(),
  /** Só objetos com etiqueta fora de remessa (para montar uma). */
  unbatched: z.enum(["yes"]).optional(),
});
export type ShipmentListQuery = z.output<typeof shipmentListQuery>;

export const shipmentFormOptionsQuery = z.object({ salesOrderId: ulidParam });

export const shipmentCancelInput = z.object({
  reason: z
    .string("Informe o motivo")
    .trim()
    .min(5, "Motivo: de 5 a 255 caracteres")
    .max(255, "Motivo: de 5 a 255 caracteres"),
});
export type ShipmentCancelInput = z.output<typeof shipmentCancelInput>;

export const shipmentReverseInput = z.object({
  serviceCode,
  serviceName: text(60, "o nome do serviço"),
  carrierName: nullish(z.string().trim().max(60, "Transportadora: até 60 caracteres")),
  /** null = o pacote do objeto de ida. */
  package: packageInput.nullish().transform((v) => v ?? null),
  notifyCustomer: z.boolean().default(true),
  reason: z
    .string("Informe o motivo")
    .trim()
    .min(3, "Motivo: de 3 a 255 caracteres")
    .max(255, "Motivo: de 3 a 255 caracteres"),
});
export type ShipmentReverseInput = z.output<typeof shipmentReverseInput>;

const idList = (max: number, what: string) =>
  z
    .array(ulidParam)
    .min(1, `Selecione ${what}`)
    .max(max, `Até ${max} ${what}`)
    .refine((l) => new Set(l).size === l.length, "Objeto repetido");

/** Sincronizar o rastreamento dos selecionados (sem ids = todos os rastreáveis da empresa, pela fila). */
export const shipmentTrackInput = z.object({ ids: idList(MAX_TRACK_SELECTION, "objetos").optional() });
export type ShipmentTrackInput = z.output<typeof shipmentTrackInput>;

export type ShipmentAddress = {
  name: string;
  document: string | null;
  email: string | null;
  phone: string | null;
  cep: string;
  street: string;
  number: string;
  complement: string | null;
  district: string;
  city: string;
  uf: string;
  ie?: string | null;
};
export type ShipmentContentItem = {
  description: string;
  qtyQ4: number;
  unitCents: number;
  code: string | null;
};

export type ShipmentEventView = {
  id: string;
  code: string | null;
  description: string;
  location: string | null;
  status: ShipmentStatus | null;
  occurredAt: string;
};

export type ShipmentListItem = {
  id: string;
  number: number;
  provider: LogisticsProvider;
  integrationId: string;
  integrationName: string;
  isReverse: boolean;
  status: ShipmentStatus;
  serviceName: string;
  carrierName: string | null;
  trackingCode: string | null;
  salesOrderId: string | null;
  salesOrderNumber: number | null;
  recipientName: string;
  recipientCity: string;
  recipientUf: string;
  weightG: number;
  declaredValueCents: number;
  freightCostCents: number | null;
  batchId: string | null;
  batchNumber: number | null;
  hasLabel: boolean;
  errorMessage: string | null;
  lastEventAt: string | null;
  createdAt: string;
};

export type ShipmentView = ShipmentListItem & {
  serviceCode: string;
  fiscalDocumentId: string | null;
  invoiceKey: string | null;
  reverseOfId: string | null;
  reverseOfNumber: number | null;
  reverses: { id: string; number: number; status: ShipmentStatus }[];
  sender: ShipmentAddress;
  recipient: ShipmentAddress;
  contents: ShipmentContentItem[];
  heightCm: number;
  widthCm: number;
  lengthCm: number;
  quotedCents: number | null;
  deliveryDays: number | null;
  externalId: string | null;
  reverseExpiresAt: string | null;
  notifyCustomer: boolean;
  notifiedAt: string | null;
  postedAt: string | null;
  deliveredAt: string | null;
  trackedAt: string | null;
  notes: string | null;
  attempts: number;
  canceledAt: string | null;
  cancelReason: string | null;
  events: ShipmentEventView[];
  /** Ações possíveis agora (a tela só mostra o que vale). */
  can: { cancel: boolean; retry: boolean; reverse: boolean; label: boolean; track: boolean };
};

export type ShipmentList = {
  data: ShipmentListItem[];
  nextCursor: string | null;
  summary: { count: number; freightCostCents: number };
};

export type ShipmentFormOptions = {
  salesOrder: {
    id: string;
    number: number;
    contactName: string;
    totalCents: number;
    itemsTotalCents: number;
  };
  recipient: ShipmentAddress | null;
  /** Problemas do endereço/remetente que impedem a etiqueta (a tela avisa antes). */
  issues: string[];
  invoice: { id: string; number: number; key: string } | null;
  suggestedPackage: PackageInput;
  declaredValueCents: number;
  activeShipment: { id: string; number: number; status: ShipmentStatus } | null;
  integrations: {
    id: string;
    name: string;
    provider: LogisticsProvider;
    defaultPackage: PackageInput | null;
  }[];
};

// ---------- remessas (antiga PLP) ----------

export const shipmentBatchCreateInput = z.object({
  integrationId: ulidParam,
  shipmentIds: idList(MAX_BATCH_SHIPMENTS, "objetos"),
  notes: nullish(z.string().trim().max(500, "Observações: até 500 caracteres")),
});
export type ShipmentBatchCreateInput = z.output<typeof shipmentBatchCreateInput>;

export const shipmentBatchLinkInput = z
  .object({
    add: z.array(ulidParam).max(MAX_BATCH_SHIPMENTS).default([]),
    remove: z.array(ulidParam).max(MAX_BATCH_SHIPMENTS).default([]),
  })
  .refine((v) => v.add.length + v.remove.length > 0, "Selecione objetos para vincular ou desvincular")
  .refine((v) => new Set([...v.add, ...v.remove]).size === v.add.length + v.remove.length, "Objeto repetido");
export type ShipmentBatchLinkInput = z.output<typeof shipmentBatchLinkInput>;

export const shipmentBatchListQuery = listQuery.extend({
  status: z.enum(["open", "closed"]).optional(),
  integrationId: ulidParam.optional(),
});
export type ShipmentBatchListQuery = z.output<typeof shipmentBatchListQuery>;

export type ShipmentBatchListItem = {
  id: string;
  number: number;
  integrationId: string;
  integrationName: string;
  provider: LogisticsProvider;
  status: "open" | "closed";
  shipmentsCount: number;
  weightG: number;
  declaredValueCents: number;
  labelsStatus: "none" | "pending" | "ready" | "error";
  closedAt: string | null;
  createdAt: string;
};
export type ShipmentBatchView = ShipmentBatchListItem & {
  notes: string | null;
  labelsError: string | null;
  shipments: ShipmentListItem[];
};
export type ShipmentBatchList = { data: ShipmentBatchListItem[]; nextCursor: string | null };

export type FileLink = { url: string };

// ---------- sandbox ----------

export const SANDBOX_TRACKING_EVENTS = [
  "posted",
  "in_transit",
  "out_for_delivery",
  "delivered",
  "failed",
  "returned",
] as const;
export const shipmentSandboxEventInput = z.object({
  event: z.enum(SANDBOX_TRACKING_EVENTS, "Evento inválido"),
});
export type ShipmentSandboxEventInput = z.output<typeof shipmentSandboxEventInput>;
